'use client';

import React from 'react';
import { motion } from 'framer-motion';

const ArrowSVG = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 25 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12.5" cy="12.5" r="12.5" fill="black" />
    <path
      d="M10 8L15 12.5L10 17"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface CategoriesBarProps {
  categories: string[];
  activeCategory: string;
  isOverflowing: boolean;
  tabsRef: React.RefObject<HTMLDivElement>;
  onSelect: (cat: string) => void;
  onScrollLeft: () => void;
  onScrollRight: () => void;
}

export default function CategoriesBar({
  categories,
  activeCategory,
  tabsRef,
  onSelect,
  onScrollLeft,
  onScrollRight,
}: CategoriesBarProps) {
  const tabClass = (active: boolean) =>
    `shrink-0 h-[6.875rem] px-[3.25rem] py-[1.5rem] rounded-[0.25rem] text-[2rem] leading-normal tracking-[-0.08rem] transition-colors font-geist border ${
      active
        ? 'bg-black text-white border-transparent'
        : 'bg-white border-black/20 text-black/60 hover:border-black/40'
    }`;

  return (
    <div className="flex items-center gap-[2.75rem] min-w-0 flex-1">
      <motion.button
        onClick={onScrollLeft}
        className="shrink-0 hidden md:block"
        aria-label="Scroll categories left"
      >
        <ArrowSVG className="w-[2.5rem] h-[2.5rem] md:w-[3.125rem] md:h-[3.125rem] rotate-180" />
      </motion.button>

      <div
        ref={tabsRef}
        className="flex items-center gap-[1.25rem] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden min-w-0 flex-1"
      >
        <button onClick={() => onSelect('')} className={tabClass(activeCategory === '')}>
          All
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => onSelect(cat)}
            className={`${tabClass(activeCategory === cat)} whitespace-nowrap`}
          >
            {cat}
          </button>
        ))}
      </div>

      <motion.button
        onClick={onScrollRight}
        className="shrink-0 hidden md:block"
        aria-label="Scroll categories right"
      >
        <ArrowSVG className="w-[2.5rem] h-[2.5rem] md:w-[3.125rem] md:h-[3.125rem]" />
      </motion.button>
    </div>
  );
}
